// Which release this tab runs, whether a newer one went out, and when the tab may move to it
// (plan frontend-release-update, 2026-10-21).
//
// A Pages deploy reaches only tabs opened after it. A tab left open on the Daily Briefing kept
// the old bundle for days, missed fixes and talked to an API that had moved on. So every built
// bundle carries its release (`__APP_RELEASE__`, vite.config.ts) and Pages publishes the
// newest one as `/version.json`; an open tab reads it now and then, and once two reads 30 s
// apart agree on a release other than its own, the update is confirmed. Then:
//
//  - the banner (components/update-banner.tsx) offers Reload;
//  - the next page change loads the new release as a full page load (`swapToConfirmedRelease`,
//    run by the root route's `beforeLoad` before any child loads);
//  - a view page hidden for two minutes reloads quietly.
//
// None of it ever loses work: in-flight work holds every swap (release-hold.ts), and a page that
// may hold an unsaved draft is replaced only by the operator's own page change or Reload. There
// is no forced update (Gio, 2026-10-21).
//
// Not server data, so not a query: a small external store, like maintenance.ts.

import { useSyncExternalStore } from 'react';
import { redirect } from '@tanstack/react-router';
import type { FileRoutesById } from '@/routeTree.gen';
import { readPublishedRelease } from '@/api/client';
import { fmtStampEt } from '@/lib/datetime';
import { releaseHeld, subscribeReleaseHolds } from '@/lib/release-hold';

/** A build of the frontend, as `version.json` publishes it and the sidebar shows it. */
export interface AppRelease {
  /** Commits in the history at build time; the label reads `v1.<number>`. */
  number: number;
  /** The short commit sha (`VITE_BUILD_SHA`); `dev` for an unstamped build, `crawl` for the crawl's. */
  sha: string;
  /** The build's New York date, `YYYYMMDD`. */
  date: string;
  /** When it was built, ISO UTC. */
  built_at: string;
}

/** `v1.1092 · bb785b7 · 20261021`. */
export function releaseLabel(release: AppRelease): string {
  return `v1.${release.number} · ${release.sha} · ${release.date}`;
}

/** `v1.1093 · 20261022`: what the banner names. */
export function releaseShortLabel(release: AppRelease): string {
  return `v1.${release.number} · ${release.date}`;
}

/** The label's tooltip: when the build was made, in New York. */
export function releaseBuiltAt(release: AppRelease): string {
  return `Built ${fmtStampEt(release.built_at)}`;
}

/**
 * `version.json` as an `AppRelease`, or null for anything else (an index.html served under
 * the name during a deploy, a truncated body). Fields beyond the four are tolerated: a later
 * release that adds one must still read as a release to tabs opened before it, or they would
 * never learn of it.
 */
export function parseRelease(json: unknown): AppRelease | null {
  if (typeof json !== 'object' || json === null || Array.isArray(json)) return null;
  const number: unknown = Reflect.get(json, 'number');
  const sha: unknown = Reflect.get(json, 'sha');
  const date: unknown = Reflect.get(json, 'date');
  const builtAt: unknown = Reflect.get(json, 'built_at');
  if (typeof number !== 'number' || !Number.isSafeInteger(number) || number < 0) return null;
  if (typeof sha !== 'string' || !/^[0-9A-Za-z]{1,40}$/.test(sha)) return null;
  if (typeof date !== 'string' || !/^\d{8}$/.test(date)) return null;
  if (
    typeof builtAt !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}T/.test(builtAt) ||
    Number.isNaN(Date.parse(builtAt))
  )
    return null;
  return { number, sha, date, built_at: builtAt };
}

// ── timing ───────────────────────────────────────────────────────────────────

/** How often a visible tab reads `version.json`. */
export const RELEASE_POLL_VISIBLE_MS = 5 * 60_000;
/** How often a hidden one does (a browser that freezes background pages stops this too). */
export const RELEASE_POLL_HIDDEN_MS = 15 * 60_000;
/**
 * A differing read is confirmed by a second one this much later: Pages reaches its edges over
 * a few seconds, and one edge's answer during a deploy is not yet the release.
 */
export const RELEASE_CONFIRM_AFTER_MS = 30_000;
/** Focus and visibility read at most this often. */
export const RELEASE_FOCUS_READ_GAP_MS = 60_000;
/** The first read, a moment after the page has painted. */
export const RELEASE_FIRST_READ_MS = 2_000;
/** A view page hidden this long with a confirmed update reloads quietly. */
export const RELEASE_QUIET_RELOAD_HIDDEN_MS = 2 * 60_000;
/** Sibling tabs on this origin: a hint carries only "I saw sha X". */
export const RELEASE_CHANNEL = 'shubert-release-v1';

// ── seams ────────────────────────────────────────────────────────────────────

/**
 * What the store does to the page and reads from it, injectable because jsdom's
 * `location.reload` cannot be replaced and Playwright's clock virtualizes time, not
 * visibility.
 */
export interface ReleaseSeams {
  reload(): void;
  visibility(): DocumentVisibilityState;
}

const DEFAULT_SEAMS: ReleaseSeams = {
  reload: () => window.location.reload(),
  visibility: () => document.visibilityState,
};
let seams: ReleaseSeams = DEFAULT_SEAMS;

/** Replace some seams (tests); null restores the browser's. */
export function setReleaseSeams(next: Partial<ReleaseSeams> | null): void {
  seams = next ? { ...DEFAULT_SEAMS, ...next } : DEFAULT_SEAMS;
}

// ── the store ────────────────────────────────────────────────────────────────

export interface ReleaseState {
  /** The release this bundle is. */
  readonly current: AppRelease;
  /** A differing release one read has seen, awaiting its confirming read. */
  readonly candidate: AppRelease | null;
  readonly candidateSince: number | null;
  /** A differing release two reads agreed on: an update is ready. */
  readonly confirmed: AppRelease | null;
  /** When the tab was last hidden, or null while it is visible. */
  readonly hiddenSince: number | null;
  /** A lazily loaded part of the app failed to load since the last read of `current`. */
  readonly chunkFailed: boolean;
  /** The release whose banner the operator closed in this tab. */
  readonly dismissed: string | null;
  /** The last resolved pathname. */
  readonly page: string | null;
  /** A form control was typed in or changed since the page changed. */
  readonly edited: boolean;
  /** A hold or a mutation is in place (release-hold.ts). */
  readonly held: boolean;
}

function initialState(): ReleaseState {
  return {
    current: __APP_RELEASE__,
    candidate: null,
    candidateSince: null,
    confirmed: null,
    hiddenSince: null,
    chunkFailed: false,
    dismissed: null,
    page: null,
    edited: false,
    held: releaseHeld(),
  };
}

let state: ReleaseState = initialState();
const listeners = new Set<() => void>();

function set(patch: Partial<ReleaseState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getReleaseState(): ReleaseState {
  return state;
}

export function useReleaseState(): ReleaseState {
  return useSyncExternalStore(subscribe, getReleaseState, getReleaseState);
}

/** Close the banner for this release, in this tab. The automatic paths still apply. */
export function dismissReleaseBanner(sha: string): void {
  set({ dismissed: sha });
}

/** The banner's Reload. */
export function reloadForRelease(): void {
  seams.reload();
}

subscribeReleaseHolds(() => {
  const held = releaseHeld();
  if (held === state.held) return;
  set({ held });
  // A quiet reload refused while work was in flight gets its turn now.
  if (!held) armDeadline();
});

// ── reading version.json ─────────────────────────────────────────────────────

let reading = false;
let readAgain = false;
let lastReadAt = Number.NEGATIVE_INFINITY;
let confirmTimer: ReturnType<typeof setTimeout> | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let deadlineTimer: ReturnType<typeof setTimeout> | null = null;
let checking = false;
let channel: BroadcastChannel | null = null;

/**
 * Read `version.json` now. One read at a time: a request made while one is out runs once more
 * after it, so results are applied in the order they were read.
 */
export function requestReleaseRead(): void {
  if (reading) {
    readAgain = true;
    return;
  }
  reading = true;
  lastReadAt = Date.now();
  const done = () => {
    reading = false;
    if (readAgain) {
      readAgain = false;
      requestReleaseRead();
    } else {
      schedulePoll();
    }
  };
  void readPublishedRelease(parseRelease).then(
    (read) => {
      noteReleaseRead(read);
      done();
    },
    () => done(),
  );
}

/** A read on focus or on becoming visible: at most once a minute. */
function readSoon(): void {
  if (Date.now() - lastReadAt < RELEASE_FOCUS_READ_GAP_MS) return;
  requestReleaseRead();
}

/** Apply one read. Exported for tests; the checker calls it with each answer in order. */
export function noteReleaseRead(read: AppRelease | null, now: number = Date.now()): void {
  // Unreadable (a deploy window, no network): nothing is learned.
  if (!read) return;
  if (read.sha === state.current.sha) {
    // The running release is the published one again (a rollback, or a stale candidate):
    // no update is left pending.
    clearConfirmTimer();
    disarmDeadline();
    if (state.candidate || state.confirmed || state.chunkFailed) {
      set({ candidate: null, candidateSince: null, confirmed: null, chunkFailed: false });
    }
    return;
  }
  if (state.confirmed?.sha === read.sha) {
    if (state.candidate) set({ candidate: null, candidateSince: null });
    clearConfirmTimer();
    return;
  }
  if (state.candidate?.sha === read.sha && state.candidateSince !== null) {
    // Too soon after the first sighting to confirm anything: the scheduled read decides.
    if (now - state.candidateSince < RELEASE_CONFIRM_AFTER_MS) return;
    clearConfirmTimer();
    set({ confirmed: read, candidate: null, candidateSince: null });
    broadcastHint(read.sha);
    armDeadline();
    return;
  }
  set({ candidate: read, candidateSince: now });
  clearConfirmTimer();
  confirmTimer = setTimeout(() => {
    confirmTimer = null;
    requestReleaseRead();
  }, RELEASE_CONFIRM_AFTER_MS);
}

/**
 * A sibling tab saw `sha`. Only a hint: this tab reads and confirms for itself, so a late or
 * stale message can never mark an update on its own.
 */
export function noteReleaseHint(sha: string): void {
  if (sha === state.current.sha || sha === state.confirmed?.sha) return;
  requestReleaseRead();
}

/**
 * A dynamic import failed (`vite:preloadError`, main.tsx). Not proof of a release: it only
 * asks at once, and a confirmed new release then says this part changed.
 */
export function noteChunkLoadFailure(): void {
  if (!state.chunkFailed) set({ chunkFailed: true });
  requestReleaseRead();
}

function clearConfirmTimer(): void {
  if (confirmTimer) clearTimeout(confirmTimer);
  confirmTimer = null;
}

function schedulePoll(): void {
  if (pollTimer) clearTimeout(pollTimer);
  pollTimer = null;
  if (!checking) return;
  const every = seams.visibility() === 'hidden' ? RELEASE_POLL_HIDDEN_MS : RELEASE_POLL_VISIBLE_MS;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    requestReleaseRead();
  }, every);
}

function broadcastHint(sha: string): void {
  try {
    channel?.postMessage({ sha });
  } catch {
    // A closed channel: the siblings poll on their own.
  }
}

// ── the quiet reload of a hidden view page ───────────────────────────────────

function disarmDeadline(): void {
  if (deadlineTimer) clearTimeout(deadlineTimer);
  deadlineTimer = null;
}

/**
 * Arm the hidden tab's reload for `hiddenSince` + two minutes. Re-armed when a release confirms
 * after the tab was hidden and when a hold ends; when it fires it asks again, from live state.
 */
function armDeadline(): void {
  disarmDeadline();
  if (state.hiddenSince === null || !state.confirmed) return;
  const delay = Math.max(0, state.hiddenSince + RELEASE_QUIET_RELOAD_HIDDEN_MS - Date.now());
  deadlineTimer = setTimeout(() => {
    deadlineTimer = null;
    if (seams.visibility() === 'hidden' && state.confirmed && reloadIsSafe()) seams.reload();
  }, delay);
}

function onVisibilityChange(): void {
  if (seams.visibility() === 'hidden') {
    if (state.hiddenSince === null) set({ hiddenSince: Date.now() });
    armDeadline();
  } else {
    if (state.hiddenSince !== null) set({ hiddenSince: null });
    disarmDeadline();
    readSoon();
  }
  schedulePoll();
}

// ── the checker ──────────────────────────────────────────────────────────────

let stopChecker: (() => void) | null = null;

/** Start polling, unless this bundle has nothing to compare (`dev`, vite.config.ts). */
export function startReleaseChecker(): void {
  if (__RELEASE_CHECK__) runReleaseChecker();
}

/** Start polling whatever the build says (tests); returns the stop function. */
export function runReleaseChecker(): () => void {
  if (stopChecker) return stopChecker;
  checking = true;
  set({ hiddenSince: seams.visibility() === 'hidden' ? Date.now() : null });
  const onFocus = () => readSoon();
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onFocus);
  if (typeof BroadcastChannel === 'function') {
    channel = new BroadcastChannel(RELEASE_CHANNEL);
    channel.onmessage = (event: MessageEvent<unknown>) => {
      const sha: unknown =
        typeof event.data === 'object' && event.data !== null
          ? Reflect.get(event.data, 'sha')
          : null;
      if (typeof sha === 'string') noteReleaseHint(sha);
    };
  }
  // Once the first screen has painted; every later read is scheduled from the last one.
  const first = setTimeout(() => requestReleaseRead(), RELEASE_FIRST_READ_MS);
  armDeadline();
  stopChecker = () => {
    checking = false;
    clearTimeout(first);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onFocus);
    channel?.close();
    channel = null;
    if (pollTimer) clearTimeout(pollTimer);
    pollTimer = null;
    clearConfirmTimer();
    disarmDeadline();
    stopChecker = null;
  };
  return stopChecker;
}

// ── which pages SAM may replace unasked ──────────────────────────────────────

/**
 * Read-only pages: lists, reports and dashboards, each audited for inline forms before it was
 * listed (2026-10-21). Their pickers are filters, their drafts live in drawers, which block a
 * reload while open, and their row actions are mutations, which hold it. Every other page (the
 * campaign builder, New Audience, New Creative, Admin Config, the planner, Creative Review, the
 * auth pages, …) moves to a new release only on the operator's own page change or Reload.
 * Invoices is not here: its Generate Period panel holds a selection in custom pickers, which
 * fire no DOM event the edit latch could see (Astra r3).
 */
const VIEW_ROUTE_IDS: readonly (keyof FileRoutesById)[] = [
  '/campaigns/',
  '/reports/',
  '/reports/delivery',
  '/reports/markup',
  '/reports/perf-halo',
  '/brief',
  '/backend/pipeline',
  '/backend/data-pipeline',
  '/backend/system-health',
  '/backend/alerts',
  '/audiences/',
  '/creatives/',
];
export const RELEASE_VIEW_ROUTES: ReadonlySet<string> = new Set(VIEW_ROUTE_IDS);

/**
 * Pages whose own `beforeunload` prompt is armed (Admin Config with unsaved changes). While one
 * is, no page change swaps: the prompt could be answered Stay on a location the router already
 * committed (Astra plan r3). It follows the prompt's own effect, not a path: React can keep a
 * left page's effects alive behind a pending skeleton, and a path or a 404's prefix match would
 * misjudge it (Astra code review, 2026-10-21).
 */
let unloadPrompts = 0;

/** Called by a page while its own `beforeunload` prompt is armed; the result disarms it. */
export function registerUnloadPrompt(): () => void {
  unloadPrompts += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    unloadPrompts -= 1;
  };
}

/** What the store reads from the router (the app's router satisfies it). */
export interface ReleaseRouterView {
  readonly state: {
    readonly location: { readonly href: string; readonly pathname: string };
    readonly resolvedLocation?: { readonly pathname: string };
    readonly matches: ReadonlyArray<{ readonly routeId: string }>;
  };
}

let router: ReleaseRouterView | null = null;
let editsWatched = false;

/**
 * Give the store the app's router (main.tsx), and watch for edits: any `input` or `change` on
 * the page sets the latch, and only a page change clears it.
 */
export function installReleaseRouter(view: ReleaseRouterView | null): void {
  router = view;
  if (editsWatched || typeof document === 'undefined') return;
  editsWatched = true;
  const onEdit = () => {
    if (!state.edited) set({ edited: true });
  };
  document.addEventListener('input', onEdit, true);
  document.addEventListener('change', onEdit, true);
}

/** A navigation resolved on `pathname` (main.tsx's onResolved). */
export function noteReleasePageResolved(pathname: string): void {
  if (state.page === pathname) return;
  set({ page: pathname, edited: false });
}

function overlayOpen(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.querySelector('dialog.responsive-overlay, dialog[open]') !== null
  );
}

/**
 * Whether SAM may replace this page without asking: a view page, no drawer or dialog open,
 * nothing typed since the page loaded, and nothing in flight.
 */
export function reloadIsSafe(): boolean {
  const leaf = router?.state.matches.at(-1)?.routeId;
  return (
    leaf !== undefined &&
    RELEASE_VIEW_ROUTES.has(leaf) &&
    !state.edited &&
    // A page's own unload prompt can outlive it behind a pending skeleton, armed by an edit no
    // input event marked (Admin Config's Clear): never replace it unasked (Astra, 2026-10-21).
    unloadPrompts === 0 &&
    !overlayOpen() &&
    !releaseHeld()
  );
}

/**
 * Whether two pathnames name the same page, as the router matches them: case-insensitively
 * and ignoring a trailing slash. A case-sensitive check let `/ADMIN/CONFIG`, canonicalized to
 * `/admin/config` by its first tab change, read as a page change and lose the Admin Config
 * exemption (Astra code review, 2026-10-21).
 */
function samePath(a: string, b: string): boolean {
  const norm = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p).toLowerCase();
  return norm(a) === norm(b);
}

/**
 * The root route's `beforeLoad`: a page change with a confirmed update lands on the new release.
 *
 * It throws a document redirect to the same URL, which router-core turns into
 * `location.replace(href)` (router.ts `navigate`, installed 1.171). History has already
 * committed the entry for a push, a replace or a Back/Forward, so the replace loads it in place,
 * and it runs before any child route loads, so nothing loads twice. Nothing happens on the first
 * load, on a search-only change (tabs, filters, an overlay's own history entry), while work is
 * held, or when leaving Admin Config.
 *
 * A hover preload of another page meets the same throw: router-core's `preloadRoute` discards a
 * document redirect, so hovering never reloads, but the preload stops before the destination's
 * loaders. Otherwise every link click read its destination twice, once in the old bundle on
 * hover and once in the new document (measured by the release flow, 2026-10-21).
 */
export function swapToConfirmedRelease(location: {
  readonly href: string;
  readonly pathname: string;
}): void {
  if (!router || !state.confirmed || releaseHeld()) return;
  // The page being left is the last resolved location, with its full pathname (a 404's leaf
  // match carries only the matched prefix). A navigation still pending is not on screen, so a
  // Back pressed before it renders returns to the page on screen: no page change. None yet is
  // the first load.
  const from = router.state.resolvedLocation;
  if (!from) return;
  if (samePath(from.pathname, location.pathname)) return;
  if (unloadPrompts > 0) return;
  throw redirect({ href: location.href, reloadDocument: true, replace: true });
}

/** Back to a fresh store (tests). */
export function resetReleaseForTests(): void {
  stopChecker?.();
  clearConfirmTimer();
  disarmDeadline();
  reading = false;
  readAgain = false;
  lastReadAt = Number.NEGATIVE_INFINITY;
  router = null;
  unloadPrompts = 0;
  seams = DEFAULT_SEAMS;
  state = initialState();
  for (const listener of listeners) listener();
}
